import type { ComplianceStatus, SubmissionDetail, SubmissionSummary } from '@palaccount/contracts';

import { amountOf, sumAmounts } from '@/lib/money';

/**
 * The drawing decisions of FIN-08 and the SAL-04 strip, as pure functions.
 *
 * Everything a reader would otherwise have to reconstruct from JSX lives
 * here and is pinned by `compliance.test.ts`: which of the three gate
 * banners shows (and that at most ONE ever does), which action a status
 * offers, whether the attention panel appears, whether the rejected count
 * card gets its red border, and how a stored payload is shortened for a
 * table cell without being altered.
 *
 * The server decides everything that MATTERS — a refusal is a refusal on the
 * server whatever this file returns (BR-FIN-08-03/-05). These functions only
 * decide what is drawn.
 */

/** FIN-08 §7 / SAL-04 §7 — the four statuses and their colours. */
export const STATUS_META: Record<
  ComplianceStatus,
  { labelKey: string; chip: string; order: number }
> = {
  pending: {
    labelKey: 'einv.stPending',
    chip: 'bg-muted text-muted-foreground',
    order: 0,
  },
  submitted: {
    labelKey: 'einv.stSubmitted',
    chip: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
    order: 1,
  },
  accepted: {
    labelKey: 'einv.stAccepted',
    chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    order: 2,
  },
  rejected: {
    labelKey: 'einv.stRejected',
    chip: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
    order: 3,
  },
};

/** §5d — three categories, each with its own colour (the prototype greyed all three). */
export const TAX_CATEGORY_META = {
  standard: { labelKey: 'einv.catStandard', chip: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  zero_rated: { labelKey: 'einv.catZeroRated', chip: 'bg-muted text-muted-foreground' },
  exempt: { labelKey: 'einv.catExempt', chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' },
} as const;

/**
 * §5b's three gate banners. "Exactly one of these can show at a time", and
 * under the reporting model NEITHER clearance banner appears at all
 * (BR-FIN-08-04) — which is the entire behavioural difference between the
 * two models, so it is one function with one return value rather than three
 * conditions scattered through the markup.
 */
export type GateBanner = 'clearance_blocked' | 'cleared' | 'rejected' | null;

export function gateBanner(
  record: Pick<SubmissionSummary, 'status' | 'submissionModel'>,
): GateBanner {
  // Banner 8 (rejected) wins: an invoice the authority refused has something
  // to say that outranks "you may not send it yet", and R1 forbids drawing
  // Resubmit anywhere without the reason beside it.
  if (record.status === 'rejected') return 'rejected';
  // BR-SAL-04-02's banner half / BR-FIN-08-04 — "under the reporting model,
  // neither clearance banner appears". This return IS that rule: the strip
  // that draws the banner only relays this verdict. (The rule's other half,
  // "and sending is not blocked", is the server's clearance gate in
  // `einvoicing.service.ts` — `sendable` below merely mirrors it.)
  if (record.submissionModel === 'reporting') return null;
  return record.status === 'accepted' ? 'cleared' : 'clearance_blocked';
}

/**
 * §6 — which action a record offers. "Neither Submitted nor Accepted offers
 * any action, which is correct: an invoice with the authority is out of your
 * hands, and an accepted one is finished" (BR-FIN-08-05).
 */
export type ComplianceAction = 'submit' | 'resubmit' | null;

export function actionFor(status: ComplianceStatus): ComplianceAction {
  if (status === 'pending') return 'submit';
  if (status === 'rejected') return 'resubmit';
  return null;
}

/**
 * BR-SAL-04-01 / BR-FIN-08-03 — may this invoice go to the customer?
 * Under clearance, only once accepted. Under reporting, always.
 */
export function sendable(
  record: Pick<SubmissionSummary, 'status' | 'submissionModel' | 'sentToCustomerAt'>,
): boolean {
  if (record.sentToCustomerAt !== null) return false;
  return record.submissionModel === 'reporting' || record.status === 'accepted';
}

/** §8 — "the rejected card gains a red border only when its count is above zero". */
export function rejectedCardBorder(count: number): boolean {
  return count > 0;
}

/** §8 — "the Needs attention panel appears only when something is rejected". */
export function showsAttention(attention: readonly unknown[]): boolean {
  return attention.length > 0;
}

/**
 * "Recent submissions … with the authority's raw response truncated."
 *
 * Truncation is display only: the full response is on the detail pane and in
 * the log for ever, and the ellipsis is added rather than substituted so a
 * reader can tell a shortened string from a short one.
 */
export function truncateResponse(raw: string | null, max = 72): string {
  if (raw === null || raw === '') return '—';
  return raw.length <= max ? raw : `${raw.slice(0, max)}…`;
}

/**
 * BR-FIN-08-11's masking rule, applied to the company's own tax number on
 * the Configuration tab: everything but the last four characters is dots.
 *
 * (The portal client-id/secret pair the rule was written for does not exist
 * in this build — no adapter has a portal yet — so this is the one field it
 * has to protect. TODO(portal-credentials): when a real adapter lands, its
 * credentials get the same treatment plus the independent reveal, stored
 * through the tenant key envelope rather than in plain settings.)
 *
 * ERR-FIN-08-06 — the prototype defect where revealing    rule-coverage:ignore
 * the client SECRET unmasked the client ID as well — is RETIRED by delegated
 * ruling D50 (27 Aug 2026, delivered by finfu--spec.patch), for exactly the
 * reason this note always recorded: the reveal it governs sits on portal
 * credentials, portal credentials belong to a REAL authority's adapter
 * (DECISION.md §8.10 — only the mock exists; ZATCA/PS/JO are its named
 * TODOs), and no credential field, column or reveal exists anywhere in this
 * build to get the defect right or wrong on. Verified by measurement, not
 * assumption: the config surface carries only `taxId`, masked above, with no
 * reveal at all. The notice carries the revival condition in the document
 * itself: when a real adapter lands and credentials are stored, independent
 * reveals get built, revealing one is tested not to unmask the other, and
 * the rule re-opens. The ignore mark stays for the claim, not the counter.
 */
export function maskToLastFour(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return '';
  if (trimmed.length <= 4) return trimmed;
  /*
   * How many bullets stand in for the hidden characters of a tax registration
   * number — STRING length arithmetic. Not an amount.
   */
  // eslint-disable-next-line money/no-raw-money-arithmetic -- string length, not an amount
  return `${'•'.repeat(Math.min(trimmed.length - 4, 12))}${trimmed.slice(-4)}`;
}

/**
 * §8's Dashboard cards, in the spec's own order: Pending, Submitted,
 * Accepted, Rejected — each clickable through to a filtered list.
 */
export const COUNT_CARDS: readonly ComplianceStatus[] = [
  'pending',
  'submitted',
  'accepted',
  'rejected',
];

/**
 * BR-FIN-08-13's footer check, as a boolean the screen can act on: net + tax
 * must equal gross. The server computes all three; this is the reader's
 * guarantee that the three figures on screen reconcile, which is exactly
 * what the prototype's footer could not claim because it never drew the
 * third one.
 */
export function breakdownReconciles(breakdown: SubmissionDetail['taxBreakdown']): boolean {
  // Decimal, not integer cents scraped off floats: `Math.round(Number(v) * 100)`
  // is the very conversion N-13 warns about, and a breakdown that reconciles to
  // the cent could fail it on a value like 8.165.
  return sumAmounts([breakdown.net, breakdown.tax]) === amountOf(breakdown.gross).toFixed2();
}
